import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfSourceViewProps {
  dealId: string;
  docId: string;
  activePage: number | null;
  activeBbox?: number[] | null;
}

export default function PdfSourceView({
  dealId,
  docId,
  activePage,
  activeBbox,
}: PdfSourceViewProps) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(activePage || 1);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
  // Actual PDF page dimensions in points (from pdf.js)
  const [pdfPageDims, setPdfPageDims] = useState<{ width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageWrapperRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const [loadError, setLoadError] = useState<string | null>(null);

  // Load PDF via authenticated client as blob URL
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    import("../../api/client").then(({ default: api }) => {
      api.get(`/api/deals/${dealId}/documents/${docId}/preview`, { responseType: "blob" })
        .then((res) => {
          if (!cancelled) {
            const blob = new Blob([res.data], { type: "application/pdf" });
            setPdfUrl(URL.createObjectURL(blob));
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLoadError("Could not load document preview. Check your connection.");
            setLoading(false);
          }
        });
    });
    return () => {
      cancelled = true;
      // Clean up blob URL
      if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
    };
  }, [dealId, docId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to active page when it changes
  useEffect(() => {
    if (activePage && activePage > 0 && activePage <= numPages) {
      setCurrentPage(activePage);
    }
  }, [activePage, numPages]);

  // Scroll highlight into view
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeBbox, currentPage, pageSize]);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoading(false);
  };

  const onPageLoadSuccess = useCallback((page: any) => {
    // Capture actual PDF page dimensions in points from pdf.js
    if (page && page.originalWidth && page.originalHeight) {
      setPdfPageDims({ width: page.originalWidth, height: page.originalHeight });
    } else if (page && page.width && page.height) {
      setPdfPageDims({ width: page.width, height: page.height });
    }
  }, []);

  const onPageRenderSuccess = useCallback(() => {
    // Capture the rendered canvas dimensions for coordinate mapping
    const canvas = pageWrapperRef.current?.querySelector("canvas");
    if (canvas) {
      setPageSize({ width: canvas.width, height: canvas.height });
    }
  }, []);

  // Compute the render width
  const renderWidth = containerRef.current?.clientWidth
    ? containerRef.current.clientWidth - 32
    : 500;

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-bark-400 p-4">
        <p className="text-sm">{loadError}</p>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-forest-600" />
      </div>
    );
  }

  // Compute highlight overlay position from PDF points to rendered pixels
  const highlightStyle = (() => {
    if (!activeBbox || activeBbox.length < 4 || !pageSize || currentPage !== activePage) {
      return null;
    }
    const [x0, top, x1, bottom] = activeBbox;

    // pdfplumber coordinates: origin top-left, units = PDF points (72/inch)
    // Scale from PDF points to rendered pixels using actual page dimensions
    const pdfW = pdfPageDims?.width || 612;
    const pdfH = pdfPageDims?.height || 792;
    const scaleX = renderWidth / pdfW;
    const scaleY = (renderWidth * (pdfH / pdfW)) / pdfH;  // preserve aspect ratio

    const left = x0 * scaleX - 3;
    const topPx = top * scaleY - 2;
    const width = (x1 - x0) * scaleX + 6;
    const height = (bottom - top) * scaleY + 4;

    return {
      position: "absolute" as const,
      left: `${Math.max(0, left)}px`,
      top: `${Math.max(0, topPx)}px`,
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: "rgba(200, 134, 10, 0.25)",
      border: "2px solid rgba(200, 134, 10, 0.6)",
      borderRadius: "3px",
      pointerEvents: "none" as const,
      zIndex: 10,
    };
  })();

  return (
    <div ref={containerRef} className="h-full flex flex-col overflow-hidden">
      {/* Page controls */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 px-3 py-2 border-b border-grove-200 bg-grove-50 shrink-0">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="p-1 rounded text-bark-400 hover:text-bark-700 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-bark-600 tabular-nums">
            Page {currentPage} of {numPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
            disabled={currentPage >= numPages}
            className="p-1 rounded text-bark-400 hover:text-bark-700 disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* PDF content */}
      <div className="flex-1 overflow-auto flex justify-center bg-bark-100 p-2">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-forest-600" />
          </div>
        )}
        <div ref={pageWrapperRef} className="relative inline-block">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() => setLoading(false)}
            loading=""
          >
            <Page
              pageNumber={currentPage}
              width={renderWidth}
              renderTextLayer={true}
              renderAnnotationLayer={false}
              onLoadSuccess={onPageLoadSuccess}
              onRenderSuccess={onPageRenderSuccess}
            />
          </Document>
          {/* Honey highlight overlay */}
          {highlightStyle && (
            <div
              ref={highlightRef}
              className="animate-pulse-once"
              style={highlightStyle}
            />
          )}
        </div>
      </div>
    </div>
  );
}
